import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import { type JSX, type SyntheticEvent, useEffect, useState } from "react";

/** Meta tag name in frontend/index.html that marks a document as Foundry Connect itself. */
export const FOUNDRY_CONNECT_APP_MARKER = "foundry-connect-app";

export const LEGACY_SIGN_IN_UNAVAILABLE =
  "Legacy sign in is unavailable, so this tab cannot open. Sign out and sign in again, then reopen it. If it still does not open, contact support.";

/**
 * Where a same origin frame ended up, or null when the browser will not say (cross origin).
 * A failed legacy sign in redirects the frame away from its own path, often to Foundry Connect
 * itself, which would otherwise render the app nested inside its own tab.
 */
const framePathname = (frame: HTMLIFrameElement): string | null => {
  try {
    return frame.contentWindow?.location.pathname ?? null;
  } catch {
    return null;
  }
};

/**
 * True when a same origin frame loaded Foundry Connect itself. A host with no legacy route
 * serves the app at the legacy path, so the pathname alone looks fine.
 */
const frameIsFoundryConnect = (frame: HTMLIFrameElement): boolean => {
  try {
    return (
      frame.contentDocument?.querySelector(
        `meta[name="${FOUNDRY_CONNECT_APP_MARKER}"]`
      ) != null
    );
  } catch {
    return false;
  }
};

export const leftLegacyPath = (
  pathname: string | null,
  prefix: string
): boolean =>
  pathname !== null &&
  pathname !== prefix &&
  !pathname.startsWith(`${prefix}/`);

/**
 * The legacy Metadata (`/vmeta`) and Portal (`/vportal`) frame, refusing to show a load that
 * left its prefix or that rendered Foundry Connect nested inside its own tab.
 */
export default function LegacyIframe({
  src,
  prefix,
}: {
  src: string;
  prefix: "/vmeta" | "/vportal";
}): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [signInFailed, setSignInFailed] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setSignInFailed(false);
  }, [src]);

  const onLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    setIsLoading(false);
    const frame = event.currentTarget;
    if (
      leftLegacyPath(framePathname(frame), prefix) ||
      frameIsFoundryConnect(frame)
    ) {
      setSignInFailed(true);
    }
  };

  if (signInFailed) {
    return (
      <div className="py-6">
        <MessageBanner
          type={BannerType.Warning}
          title="Legacy sign in unavailable"
          messages={LEGACY_SIGN_IN_UNAVAILABLE}
        />
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      )}
      <iframe
        src={src}
        style={{ height: "78dvh", width: "100%", marginLeft: "-20px" }}
        onLoad={onLoad}
      />
    </>
  );
}
